import * as React from "react";
import { Tooltip as TooltipPrimitive } from "radix-ui";

import { cn } from "@/lib/utils";
import { definitionTooltipStore, isOpen, useDefinitionTooltipState } from "@/lib/definitionTooltip";

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance",
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="bg-foreground fill-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

let definitionCount = 0;

/**
 * A definition a user can OPEN, not only hover (BEN-141 P28, #803).
 *
 * `Tooltip` above is Radix's, which opens on hover and on focus. That is right for
 * a hint and wrong for a definition: a phone has no hover, so a definition behind
 * one does not exist on a phone, and the figures these define are the ones #803
 * says people are misreading. This adds the click path, keeps hover and focus, and
 * keeps Escape, which Radix already reports through `onOpenChange`.
 *
 * The open state lives in `@/lib/definitionTooltip` — app-wide, because "only one
 * open at a time" is a statement about the page, and because the state machine is
 * then testable without a DOM (this package renders with `react-dom/server`).
 *
 * Pass `id` when two triggers define the same term on one screen and the test
 * needs to name them; otherwise a stable one is generated per instance.
 */
function DefinitionTooltip({
  children,
  label,
  side,
  sideOffset,
  className,
  glyphClassName = "h-3.5 w-3.5",
  contentClassName,
  id,
}: {
  /** The definition itself. */
  children: React.ReactNode;
  /** What the control announces, e.g. "About Balance". */
  label: string;
  side?: React.ComponentProps<typeof TooltipPrimitive.Content>["side"];
  sideOffset?: number;
  /** Classes for the ⓘ button: colour, hover and any margin the call site wants. */
  className?: string;
  /**
   * The size of the ⓘ mark itself, e.g. `h-3 w-3`.
   *
   * Separate from `className` on purpose. The button is always at least 24px
   * (WCAG 2.5.8) and the mark is 12-16px, so one class string cannot say both:
   * an `h-3` next to the button's own sizing is a coin toss over which utility
   * Tailwind emits last. The mark's size belongs to the mark.
   */
  glyphClassName?: string;
  contentClassName?: string;
  id?: string;
}) {
  const generated = React.useRef<string | undefined>(undefined);
  generated.current ??= `definition-${(definitionCount += 1)}`;
  const key = id ?? generated.current;

  const state = useDefinitionTooltipState();
  const open = isOpen(state, key);

  return (
    <TooltipPrimitive.Root
      data-slot="tooltip"
      open={open}
      onOpenChange={(next) =>
        definitionTooltipStore.dispatch({
          // Radix reports hover, focus AND Escape through this one callback. A
          // close it asks for is either the pointer leaving or Escape; `hoverClose`
          // keeps a pinned tooltip open, and the Escape handler below closes it.
          type: next ? "hoverOpen" : "hoverClose",
          id: key,
        })
      }
    >
      <TooltipPrimitive.Trigger asChild data-slot="tooltip-trigger">
        <button
          type="button"
          aria-label={label}
          aria-expanded={open}
          onClick={() => definitionTooltipStore.dispatch({ type: "toggle", id: key })}
          onKeyDown={(event) => {
            if (event.key === "Escape") definitionTooltipStore.dispatch({ type: "escape" });
          }}
          // 44px of hit area around a 12-16px mark (doc 3 H6; it was 24). `-m-3.5`
          // gives 28px of that back to the layout, the same 16px footprint the
          // 24px box with `-m-1` had, so a mark inside a line of 11px table header
          // text still sits there and the row keeps its height.
          className={cn(
            "tap-target tap-target-44 -m-3.5 inline-flex items-center justify-center align-middle",
            className,
          )}
        >
          <span className={cn("inline-flex shrink-0", glyphClassName)}>
            <InfoGlyph />
          </span>
        </button>
      </TooltipPrimitive.Trigger>
      <TooltipContent
        side={side}
        sideOffset={sideOffset}
        className={contentClassName}
        // A pinned tooltip must survive the click that opened it; without this
        // Radix closes on the pointer-down that follows a tap.
        onPointerDownOutside={() => definitionTooltipStore.dispatch({ type: "escape" })}
        onEscapeKeyDown={() => definitionTooltipStore.dispatch({ type: "escape" })}
      >
        {children}
      </TooltipContent>
    </TooltipPrimitive.Root>
  );
}

/** The ⓘ mark. Inline rather than an icon import so the primitive owns its look. */
function InfoGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-full w-full"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, DefinitionTooltip };
